import { parseISO } from "date-fns";

// A doctor may have at most this many appointments overlapping at the same
// instant (e.g. one overbooked slot on top of an existing one). A third
// concurrent appointment is never allowed, regardless of practice settings.
export const MAX_CONCURRENT_APPOINTMENTS = 2;

// Exceptions (days off, breaks) are not "appointments" — the overlapping-
// appointments rule only concerns real bookings, so these are ignored here.
const isExceptionMeeting = (meeting) =>
  meeting?.type === "standard-exception" ||
  meeting?.type === "recurring-exception";

// Two half-open intervals [startA, endA) and [startB, endB) overlap when each
// starts strictly before the other ends. Touching edges (one ends exactly when
// the next starts) is NOT an overlap.
const intervalsOverlap = (startA, endA, startB, endB) =>
  startA < endB && endA > startB;

const toDate = (value) => (value instanceof Date ? value : parseISO(value));

// Returns the existing meetings that a new/edited appointment would clash with.
// `schedule` is a doctor's schedule as normalised by useAppointments (each
// meeting has ISO `start`/`end` strings and a `consultation.id`).
export const findOverlappingAppointments = ({
  schedule = [],
  startAt,
  endAt,
  excludeAppointmentId,
}) => {
  const newStart = toDate(startAt);
  const newEnd = toDate(endAt);

  return schedule.filter((meeting) => {
    if (isExceptionMeeting(meeting)) return false;

    // When editing, the appointment must not clash with itself.
    const meetingId = meeting?.consultation?.id;

    if (
      excludeAppointmentId != null &&
      meetingId != null &&
      String(meetingId) === String(excludeAppointmentId)
    ) {
      return false;
    }

    return intervalsOverlap(
      newStart,
      newEnd,
      toDate(meeting.start),
      toDate(meeting.end)
    );
  });
};

// Peak number of the given meetings active at the same instant within the
// [startAt, endAt) window. A sweep line over clipped start/end boundaries:
// at a shared instant, ends (-1) are processed before starts (+1) so meetings
// that merely touch are not counted as concurrent.
export const getMaxOverlapDepth = ({ overlaps = [], startAt, endAt }) => {
  const windowStart = toDate(startAt);
  const windowEnd = toDate(endAt);

  const events = [];

  overlaps.forEach((meeting) => {
    const clippedStart = Math.max(
      toDate(meeting.start).getTime(),
      windowStart.getTime()
    );
    const clippedEnd = Math.min(
      toDate(meeting.end).getTime(),
      windowEnd.getTime()
    );

    if (clippedStart < clippedEnd) {
      events.push({ time: clippedStart, delta: 1 });
      events.push({ time: clippedEnd, delta: -1 });
    }
  });

  events.sort((a, b) => a.time - b.time || a.delta - b.delta);

  let current = 0;
  let peak = 0;

  events.forEach((event) => {
    current += event.delta;
    if (current > peak) peak = current;
  });

  return peak;
};
